
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Grid,
} from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import './styles.css';
import Breadcrumbs from '../../components/Breadcrumbs';
import SelectYear from '../../components/Dashboard2/SelectYear';
import SelectMonth from '../../components/Dashboard2/SelectMonth';
import SelecGroup from '../../components/Dashboard2/SelectGroup';
import api from '../../services/api';
import UserPermission from '../../components/UserPermission';
import OccurrenceByStatus from '../../components/Dashboard2/OccurrenceByStatus';
import StatusOfOccurrences from '../../components/Dashboard2/StatusOfOccurrences';
import EndOfOccurrences from '../../components/Dashboard2/EndOfOccurrences';
import OccurrencesByNeighborhood from '../../components/Dashboard2/OccurrencesByNeighborhood';
import OccurrencesByGroup from '../../components/Dashboard2/OccurrencesByGroup';
import OccurenceByService from '../../components/Dashboard2/OccurenceByService';
import moment from 'moment';

import MultiManual from '../../components/Manual/MultiManual';
import manualJson from './manual.json';
import usePollingInterval from '../../hooks/usePollingInterval';


ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const classes = {
  grid: {
    backgroundColor: '#F5F5F5',
    backgroundClip: 'content-box'
  },
  select: {
    maxHeigth: '100%'
  }
}

const Dashboard2 = () => {

  const userPermission = UserPermission();


  const [years, setYears] = useState(
    //Cria um array com os objetos {name, isActive} de 2021 até o ano atual
    //E ativa automaticamente o ano de 2021
    Array.from({ length: new Date().getFullYear() - 2020 }, (_, idx) => ({
      name: 2021 + idx,
      isActive: true,
    }))
  );

  const [months, setMonths] = useState([
    { name: 'Jan', isActive: true },
    { name: 'Fev', isActive: true },
    { name: 'Mar', isActive: true },
    { name: 'Abr', isActive: true },
    { name: 'Mai', isActive: true },
    { name: 'Jun', isActive: true },
    { name: 'Jul', isActive: true },
    { name: 'Ago', isActive: true },
    { name: 'Set', isActive: true },
    { name: 'Out', isActive: true },
    { name: 'Nov', isActive: true },
    { name: 'Dez', isActive: true },
  ]);

  const [groups, setGroups] = useState([]);

  const [occurrences, setOccurrences] = useState([]);

  const [refreshTrigger, setRefreshTrigger] = useState(0);
  usePollingInterval(() => setRefreshTrigger(t => t + 1));

  //Evita empilhar requisições do poll quando a busca anterior (também de poll) ainda não terminou.
  //Ações do usuário (mudar ano/mês/grupo) nunca são bloqueadas por esse guard.
  const isPollFetchingRef = useRef(false);

  useEffect(() => {
    const filter = {
      order: 'nameSecretary',//Necessário para o funcionamento do Grafico
    }

    api.get(`groups?filter=${JSON.stringify(filter)}`)
      .then(response => {
        const data = response.data.map((group, idx) => ({ name: group.nameSecretary, isActive: true }));
        setGroups(data);
      })
      .catch(error => {
        console.log(error);
      })
  }, []);

  const fetchOccurrences = useCallback(async ({ isPoll = false } = {}) => {
    if (isPoll) {
      if (isPollFetchingRef.current) return;
      isPollFetchingRef.current = true;
    }

    try {
      //Para verificar se deve ignorar o ano basta saber se há algum ano selecionado
      const ignoreYear = years.filter(y => y.isActive).length === 0;
      const ignoreMonth = months.filter(y => y.isActive).length === 0;

      const monthsList = months.reduce((array, month) => {
        const monthsNames = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

        if (month.isActive || ignoreMonth)
          array.push(monthsNames.indexOf(month.name));
        return array;
      }, []);

      const groupsList = groups.reduce((array, group) => {
        if (group.isActive)
          array.push(group.name);
        return array;
      }, []);

      const dateFilter = years.reduce((array, year) => {
        if (!year.isActive && !ignoreYear)
          return array;

        const and = monthsList.map(month => ({
          createdAt: {
            "between": [
              moment([year.name, month]).startOf('month').toISOString(),
              moment([year.name, month]).endOf('month').toISOString()
            ]
          }
        }))

        return array.concat(and);
      }, [])

      const filter = {
        order: 'createdAt',//Necessário para o funcionamento do Grafico
        where: {
          or: dateFilter,
        }
      }

      if (groupsList.length)
        filter.where.group = {
          "inq": groupsList
        }

      const response = await api.get(`${userPermission}/complaints?filter=${JSON.stringify(filter)}`);
      setOccurrences(response.data);
    } catch (error) {
      console.log(error);
    } finally {
      if (isPoll) isPollFetchingRef.current = false;
    }
  }, [years, months, groups, userPermission]);

  //Ação do usuário (filtros mudaram): busca sempre, sem passar pelo guard do poll
  useEffect(() => {
    window.scrollTo(0, 0);
    fetchOccurrences();
  }, [fetchOccurrences]);

  //Tick do poll: busca guardada, cede a uma ação do usuário em andamento
  useEffect(() => {
    if (refreshTrigger) {
      fetchOccurrences({ isPoll: true });
    }
  }, [refreshTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <div style={{ margin: 20 }}>
        <Breadcrumbs
          paths={[
            { href: '/dashboard2', name: 'Dashboard2' },
          ]}
        />
        <Grid container style={{ marginTop: 20, marginLeft: 0 }} spacing={2} >
          <Grid container item xs={12} sm={6} md={3} lg={3} height={160} spacing={2} style={classes.grid}>
            <SelectYear years={years} style={classes.select} setYears={setYears} />
          </Grid>

          <Grid container item xs={12} sm={6} md={3} lg={3} height={160} spacing={2} style={classes.grid}>
            <SelectMonth months={months} style={classes.select} setMonths={setMonths} />
          </Grid>

          <Grid container item xs={12} sm={12} md={6} lg={6} height={160} spacing={2} style={classes.grid}>
            <SelecGroup groups={groups} style={classes.select} setGroups={setGroups} />
          </Grid>

          <Grid container item xs={12} sm={6} md={4} lg={4} spacing={2}>
            <OccurrenceByStatus occurrences={occurrences} />
          </Grid>

          <Grid container item xs={12} sm={6} md={4} lg={4} spacing={2}>
            <StatusOfOccurrences occurrences={occurrences} months={months} />
          </Grid>

          <Grid container item xs={12} sm={12} md={4} lg={4} spacing={1.9}>
            <EndOfOccurrences occurrences={occurrences} months={months} />
          </Grid>

          <Grid container item xs={12} sm={12} md={12} lg={12} spacing={2}>
            <OccurrencesByNeighborhood occurrences={occurrences} />
          </Grid>

          <Grid container item xs={24} sm={8} md={6} lg={6} spacing={2}>
            <OccurrencesByGroup occurrences={occurrences} groups={groups} />
          </Grid>

          <Grid container item xs={24} sm={8} md={6} lg={6} spacing={2}>
            <OccurenceByService occurrences={occurrences} groups={groups} />
          </Grid>
          
        </Grid>
        
        <MultiManual components={manualJson} />
      </div >
    </>
  );
}
export default Dashboard2;
